// @flow
import { Fragment } from 'react';
import styled from '@emotion/styled';
import { SPACING, typography, FONT_WEIGHTS } from '@tablekit/theme';
import uniq from 'lodash/uniq';
import HeroBanner from 'components/HeroBanner';
import { EDITOR_VALUES, handleEditorValues } from 'components/PageContext';
import { imageTransform } from 'components/Image';
import type {
  BlokComponentType,
  CategoryType,
  IconWithTitleType
} from 'components/types';
import { LAPTOP_BREAKPOINT, MOBILE_BREAKPOINT } from 'utils/theme';
import { PostBody } from './styled';

type PropsType = {
  categories: CategoryType[],
  icons: BlokComponentType<IconWithTitleType>[],
  image: string,
  subtitle?: string,
  title: string,
  userText?: string
};

const HeroContent = styled(PostBody)`
  color: white;
  margin: ${SPACING.L8} auto;
  padding: 0;

  @media ${MOBILE_BREAKPOINT} {
    margin: 0 0 ${SPACING.L8};
  }
`;
const ExpandedHeroContent = styled(PostBody)`
  margin: 0 !important;
  padding: 0 0 ${SPACING.L8};
  color: white;

  @media ${MOBILE_BREAKPOINT} {
    padding: 0 0 ${SPACING.L6};
  }
`;
const HeroCategories = styled.div`
  ${typography('L4')};
  margin-bottom: ${SPACING.L2};
  @media ${MOBILE_BREAKPOINT} {
    ${typography('L2')};
    margin: 0;
  }
  font-weight: ${FONT_WEIGHTS.BOLD};
`;

const HeroTitle = styled.h1`
  color: white;
  ${typography('L8')};
  font-weight: ${FONT_WEIGHTS.BOLD};

  @media ${LAPTOP_BREAKPOINT} {
    ${typography('L10')};
  }
`;
const HeroSubtitle = styled.h3`
  ${typography('L6')};
  margin-top: ${SPACING.L4};

  @media ${MOBILE_BREAKPOINT} {
    margin-top: ${SPACING.L1};
    ${typography('L5')};
  }
`;
const HeroReference = styled.p`
  ${typography('L3')};
  margin-top: ${SPACING.L2};

  @media ${MOBILE_BREAKPOINT} {
    margin-top: ${SPACING.L1};
    ${typography('L2')};
  }
`;
const columns = ({ count }) => {
  let cols = '1fr';
  for (let i = 1; i < count; i += 1) {
    cols += ' 1fr';
  }
  return cols;
};
const HeroIcons = styled.div`
  display: -ms-grid;
  display: grid;
  -ms-grid-columns: ${columns};
  grid-template-columns: ${columns};
  margin: 0 auto ${SPACING.L4};

  @media ${MOBILE_BREAKPOINT} {
    -ms-grid-columns: 50% 50%;
    grid-template-columns: 50% 50%;
    margin: 0 0 ${SPACING.L4};
    width: 100%;
  }
`;

const HeroIcon = styled.div`
  -ms-grid-column-span: 1;
  grid-column-end: span 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  max-width: 320px;
  color: white;
  margin: 0;
  padding: ${SPACING.L3};
  @media ${MOBILE_BREAKPOINT} {
    max-width: 180px;
    padding: ${SPACING.L4} 0 0;
  }
`;
const HeroIconImage = styled.img`
  width: ${SPACING.L6};
  height: ${SPACING.L6};

  @media ${MOBILE_BREAKPOINT} {
    width: ${SPACING.L4};
    height: ${SPACING.L4};
  }
`;
const HeroIconTitle = styled.div`
  font-weight: ${FONT_WEIGHTS.BOLD};
  ${typography('L6')};
  @media ${MOBILE_BREAKPOINT} {
    ${typography('L1')};
    margin-top: ${SPACING.L2};
  }
`;

const BlogPostHeroBanner = ({
  image,
  categories,
  title,
  subtitle,
  userText,
  icons
}: PropsType) => {
  let mappedCategories = categories.map(({ name }) => name).join(', ');
  if (CONFIG.storyblok.version === 'draft') {
    mappedCategories = uniq(
      categories.map((categoryBlok) => {
        const result = handleEditorValues(EDITOR_VALUES.CATEGORY, categoryBlok);
        return result ? result.name : 'Invalid Category';
      })
    ).join(', ');
  }
  let ContentWrapper = HeroContent;
  if (subtitle || userText) {
    ContentWrapper = ExpandedHeroContent;
  }

  return (
    <HeroBanner image={image} isCurved={false}>
      <Fragment>
        <ContentWrapper>
          {mappedCategories && (
            <HeroCategories>{mappedCategories}</HeroCategories>
          )}
          <HeroTitle
            level={{
              [MOBILE_BREAKPOINT]: !subtitle ? 'L9' : 'L7',
              default: 'L10'
            }}
          >
            {title}
          </HeroTitle>
          {!!subtitle && <HeroSubtitle>{subtitle}</HeroSubtitle>}
          {!!userText && <HeroReference>{userText}</HeroReference>}
        </ContentWrapper>
        {!!icons && icons.length > 0 && (
          <HeroIcons count={icons.length}>
            {icons.map(({ icon, title: iconTitle }) => (
              <HeroIcon key={icon + iconTitle}>
                <HeroIconImage src={imageTransform(icon)} alt={iconTitle} />
                <HeroIconTitle>{iconTitle}</HeroIconTitle>
              </HeroIcon>
            ))}
          </HeroIcons>
        )}
      </Fragment>
    </HeroBanner>
  );
};

export default BlogPostHeroBanner;
